/**
 * Marks what changed between two rendered versions of the report, Google Docs style.
 *
 * Both versions are rendered by the same builder from the same live project data, so only the
 * report's own content differs. Each document is split into blocks (paragraphs, list items,
 * headings, table rows, images, innermost divs) and the two block sequences are aligned by content (longest common
 * subsequence, so content pushed onto another page still lines up). Between two aligned blocks:
 * - an edited block (a new block standing where an old block of the same kind was) gets a
 *   word-level diff inline: added words highlighted, removed words struck through in red, the
 *   block's own markup kept;
 * - brand-new blocks are highlighted whole;
 * - removed blocks are put back, struck through in red, where they used to be.
 *
 * Photo cards on the photos pages (image + caption) are one block each, so a caption never comes
 * apart from its image. A card whose image is the same but whose caption changed gets a caption
 * diff; a new image is a new card, and a removed one is put back as a red "Photo removed" card in
 * its slot — a replaced photo therefore shows as two cards, the removed one and the new one.
 */

// Innermost divs count too: parts of the report (the cover page, for one) are laid out with divs only.
const BLOCK_SELECTOR = 'p, li, h1, h2, h3, h4, h5, h6, tr, img, div';
const ADDED_BLOCK_CLASS = 'vh-added';
const REMOVED_BLOCK_CLASS = 'vh-removed';
const INSERTED_TEXT_CLASS = 'vh-ins';
const DELETED_TEXT_CLASS = 'vh-del';
const REMOVED_MARKER_CLASS = 'vh-removed-marker';
const ADDED_PHOTO_CLASS = 'vh-photo-added';
const REMOVED_PHOTO_BOX_CLASS = 'vh-photo-removed-box';
const PHOTO_CARD_SELECTOR = '.photo';

/** Attribute on the first marked element, to scroll it into view. */
export const FIRST_CHANGE_ATTRIBUTE = 'data-vh-first-change';

// Above this many block pairs the LCS table gets too big to build in the browser; blocks are then
// matched by content alone (no ordering), which still finds added and removed blocks.
const MAX_BLOCK_CELLS = 4_000_000;
// An edited block with more word pairs than this is highlighted whole instead of word by word.
const MAX_WORD_CELLS = 250_000;

const HIGHLIGHT_STYLES = `
  .${INSERTED_TEXT_CLASS} { background-color: #fff3b0 !important; color: inherit !important; text-decoration: none !important; }
  .${DELETED_TEXT_CLASS} { color: #d32f2f !important; text-decoration: line-through !important; }
  .${ADDED_BLOCK_CLASS} { background-color: #fff3b0 !important; }
  tr.${ADDED_BLOCK_CLASS} > td, tr.${ADDED_BLOCK_CLASS} > th { background-color: #fff3b0 !important; }
  /* Drawn inside the image, so frames with overflow: hidden don't clip it. */
  img.${ADDED_BLOCK_CLASS} { outline: 5px solid #f5c400; outline-offset: -5px; }
  .${REMOVED_BLOCK_CLASS}, .${REMOVED_BLOCK_CLASS} * { color: #d32f2f !important; text-decoration: line-through !important; }
  .${ADDED_PHOTO_CLASS} img { outline: 5px solid #f5c400; outline-offset: -5px; }
  /* The caption box is full width; its text (wrapped by wrapLooseFlexText) is sized to fit, so mark that. */
  .${ADDED_PHOTO_CLASS} .caption > * { background-color: #fff3b0 !important; }
  .${REMOVED_PHOTO_BOX_CLASS} { width: 100%; height: 100%; box-sizing: border-box; border: 4px solid #d32f2f; background: #fdecea;
    color: #d32f2f; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; text-decoration: none !important; }
  .${REMOVED_MARKER_CLASS} { box-shadow: 0 -3px 0 #d32f2f; }
  tr.${REMOVED_MARKER_CLASS} > td, tr.${REMOVED_MARKER_CLASS} > th { border-top: 3px solid #d32f2f !important; }
`;

type BlockKind = 'row' | 'photo' | 'image' | 'other';

interface Block {
  element: Element;
  key: string;
  text: string;
  kind: BlockKind;
  /** Rows: the number of cells. Removed rows only go back among rows with the same count. */
  cells: number;
  /** Photos and images: which image file. */
  image: string;
}

/** Photo URLs are signed per request, so compare the file, not the query string. */
const imageKey = (src: string) => (src.startsWith('data:') ? `${src.length}:${src.slice(-64)}` : src.split('?')[0]);

const isBlock = (element: Element) => {
  // A photo card is one block; nothing inside it is compared on its own.
  if (element.parentElement?.closest(PHOTO_CARD_SELECTOR)) return false;
  if (element.matches(PHOTO_CARD_SELECTOR)) return true;
  // A table row is one block: what's inside it is compared as part of the row.
  if (element.tagName !== 'TR' && element.parentElement?.closest('tr')) return false;
  // Otherwise only innermost blocks, so a change isn't counted twice.
  return element.tagName === 'TR' || element.tagName === 'IMG' || !element.querySelector(BLOCK_SELECTOR);
};

const kindOf = (element: Element): BlockKind =>
  element.matches(PHOTO_CARD_SELECTOR) ? 'photo' : element.tagName === 'TR' ? 'row' : element.tagName === 'IMG' ? 'image' : 'other';

const collectBlocks = (doc: Document): Block[] =>
  Array.from(doc.body.querySelectorAll(`${BLOCK_SELECTOR}, ${PHOTO_CARD_SELECTOR}`))
    .filter(isBlock)
    .flatMap(element => {
      const text = textOf(element).replace(/\s+/g, ' ').trim();
      const image = (element.tagName === 'IMG' ? [element] : Array.from(element.querySelectorAll('img')))
        .map(img => imageKey(img.getAttribute('src') || ''))
        .join('|');
      const kind = kindOf(element);
      // Empty blocks (spacers) carry no content to compare; an empty photo card still counts.
      if (!text && !image && kind !== 'photo') return [];
      return [
        {
          element,
          text,
          kind,
          image,
          cells: kind === 'row' ? element.children.length : 0,
          key: `${element.tagName}:${kind}:${text}:${image}`,
        },
      ];
    });

/**
 * Text sitting directly in a flex container (photo captions, for one) is laid out as a flex item;
 * highlight spans inserted into it would each become a separate column. Wrap it in one span first.
 */
const wrapLooseFlexText = (doc: Document) => {
  doc.body.querySelectorAll<HTMLElement>('.caption, [style*="display: flex"], [style*="display:flex"]').forEach(element => {
    const hasLooseText = Array.from(element.childNodes).some(node => node.nodeType === 3 && node.textContent?.trim());
    if (!hasLooseText || element.querySelector(BLOCK_SELECTOR)) return;
    const span = doc.createElement('span');
    while (element.firstChild) span.appendChild(element.firstChild);
    element.appendChild(span);
  });
};

/** Longest common subsequence of two sequences, as index pairs in order. */
const lcsPairs = <T>(a: T[], b: T[], equal: (x: T, y: T) => boolean): [number, number][] => {
  // lcs[i][j] = length of the LCS of a[i..] and b[j..]
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = equal(a[i], b[j]) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const pairs: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (equal(a[i], b[j])) pairs.push([i++, j++]);
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) i++;
    else j++;
  }
  return pairs;
};

/** Aligned (current, previous) block index pairs, in order. */
const matchBlocks = (current: Block[], previous: Block[]): [number, number][] => {
  if (current.length * previous.length <= MAX_BLOCK_CELLS) return lcsPairs(current, previous, (a, b) => a.key === b.key);

  const unmatched = new Map<string, number[]>();
  previous.forEach((block, index) => unmatched.set(block.key, [...(unmatched.get(block.key) ?? []), index]));
  const matchedCurrent: number[] = [];
  const matchedPrevious: number[] = [];
  current.forEach((block, index) => {
    const candidate = unmatched.get(block.key)?.shift();
    if (candidate === undefined) return;
    matchedCurrent.push(index);
    matchedPrevious.push(candidate);
  });
  matchedPrevious.sort((a, b) => a - b);
  return matchedCurrent.map((c, k) => [c, matchedPrevious[k]]);
};

// ── Word-level diff inside an edited block ──────────────────────────────────────────────────────

interface Word {
  word: string;
  start: number;
  end: number;
}

/** Words with their character offsets. Whitespace is never compared: layout can differ, words can't. */
const wordsOf = (text: string): Word[] =>
  Array.from(text.matchAll(/\S+/g), m => ({ word: m[0], start: m.index ?? 0, end: (m.index ?? 0) + m[0].length }));

/**
 * The block's text nodes with their offsets, leaving out charts: SVG text (axis labels, legends) is
 * never compared or marked, because HTML markup inside an SVG breaks the chart apart.
 */
const textNodesOf = (element: Element) => {
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const nodes: { node: Text; start: number }[] = [];
  let position = 0;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.parentElement?.closest('svg')) continue;
    nodes.push({ node: node as Text, start: position });
    position += (node as Text).data.length;
  }
  return nodes;
};

/** The block's comparable text (charts left out); offsets into it match textNodesOf. */
const textOf = (element: Element) =>
  textNodesOf(element)
    .map(({ node }) => node.data)
    .join('');

/** Wraps the text between two character offsets of `element` in highlight spans. */
const wrapRange = (element: Element, start: number, end: number) => {
  textNodesOf(element).forEach(({ node, start: nodeStart }) => {
    const from = Math.max(start, nodeStart) - nodeStart;
    const to = Math.min(end, nodeStart + node.data.length) - nodeStart;
    if (from >= to || !node.data.slice(from, to).trim()) return;
    if (to < node.data.length) node.splitText(to);
    const target = from > 0 ? node.splitText(from) : node;
    const span = element.ownerDocument.createElement('span');
    span.className = INSERTED_TEXT_CLASS;
    target.parentNode?.insertBefore(span, target);
    span.appendChild(target);
  });
};

/**
 * Inserts struck-through removed text right before the word starting at `offset` (or right after
 * the word ending at `offset`), inside that word's own element, so it stays inline with the text
 * that replaced it — even inside a flex row, where a loose element would become a column of its own.
 */
const insertDeleted = (element: Element, offset: number, text: string, position: 'before' | 'after') => {
  const doc = element.ownerDocument;
  const del = doc.createElement('del');
  del.className = DELETED_TEXT_CLASS;
  del.textContent = text;
  const gap = doc.createTextNode(' ');

  const nodes = textNodesOf(element);
  const host =
    position === 'before'
      ? nodes.find(({ node, start }) => offset >= start && offset < start + node.data.length)
      : nodes.find(({ node, start }) => offset > start && offset <= start + node.data.length);
  if (!host) {
    element.append(gap, del);
    return;
  }

  let at = offset - host.start;
  let ref: Node = host.node;
  if (position === 'before' && at > 0) {
    ref = host.node.splitText(at);
    at = 0;
  }
  if (position === 'after' && at < host.node.data.length) host.node.splitText(at);

  // Step out of a highlighted run, so removed text is never shown as added.
  const climb = (node: Node, edge: 'first' | 'last') => {
    let current = node;
    while (
      current.parentElement?.classList.contains(INSERTED_TEXT_CLASS) &&
      (edge === 'first' ? current.parentElement.firstChild === current : current.parentElement.lastChild === current)
    )
      current = current.parentElement;
    return current;
  };

  if (position === 'before') {
    const anchor = climb(ref, 'first');
    anchor.parentNode?.insertBefore(del, anchor);
    anchor.parentNode?.insertBefore(gap, anchor);
  } else {
    const anchor = climb(host.node, 'last');
    anchor.parentNode?.insertBefore(del, anchor.nextSibling);
    anchor.parentNode?.insertBefore(gap, del);
  }
};

/**
 * Marks the words `element` gained and lost compared with `oldElement`, keeping its markup. A
 * mostly rewritten block shows its old text struck through ahead of the new text. Returns false
 * (nothing marked) when the block is too long to diff word by word.
 */
const markEditedText = (element: Element, oldElement: Element): boolean => {
  const newWords = wordsOf(textOf(element));
  const oldWords = wordsOf(textOf(oldElement));
  if (newWords.length * oldWords.length > MAX_WORD_CELLS) return false;

  let pairs = lcsPairs(newWords, oldWords, (a, b) => a.word === b.word);
  // Too little in common for a word-by-word diff to read well: show it as replaced whole.
  if (Math.max(newWords.length, oldWords.length) > 0 && pairs.length / Math.max(newWords.length, oldWords.length) < 0.3) pairs = [];

  // Between two matched words: new words were added, old words were removed (one run each).
  type Change = { at: number; kind: 'added'; end: number } | { at: number; kind: 'removed'; text: string; position: 'before' | 'after' };
  const changes: Change[] = [];
  const bounds: [number, number][] = [[-1, -1], ...pairs, [newWords.length, oldWords.length]];
  for (let k = 1; k < bounds.length; k++) {
    const [newFrom, oldFrom] = [bounds[k - 1][0] + 1, bounds[k - 1][1] + 1];
    const [newTo, oldTo] = bounds[k];
    const added = newWords.slice(newFrom, newTo);
    const removed = oldWords.slice(oldFrom, oldTo);

    if (added.length > 0) changes.push({ at: added[0].start, kind: 'added', end: added[added.length - 1].end });
    if (removed.length === 0) continue;

    const text = removed.map(w => w.word).join(' ');
    // In front of what replaced it, else in front of the next word, else after the previous one.
    const nextWord = added[0] ?? newWords[newTo];
    const previousWord = newWords[newFrom - 1];
    if (nextWord) changes.push({ at: nextWord.start, kind: 'removed', text, position: 'before' });
    else if (previousWord) changes.push({ at: previousWord.end, kind: 'removed', text, position: 'after' });
    else changes.push({ at: 0, kind: 'removed', text, position: 'after' });
  }

  // Apply from the end so earlier offsets stay valid. At the same offset the added run is wrapped
  // first, so the removed text then goes in front of the wrapper.
  changes
    .sort((a, b) => b.at - a.at || (a.kind === 'added' ? -1 : 1))
    .forEach(change => {
      if (change.kind === 'added') wrapRange(element, change.at, change.end);
      else insertDeleted(element, change.at, change.text, change.position);
    });
  return true;
};

/**
 * Marks an edited block against the block it replaced. Table rows are compared cell by cell, so a
 * changed amount marks that amount, not the whole row. Returns false when it can't be marked
 * (rows with a different number of cells, or text too long to diff).
 */
const markEditedBlock = (block: Block, old: Block): boolean => {
  if (block.kind === 'photo') {
    // Same image, caption changed: diff the caption.
    const caption = block.element.querySelector('.caption');
    const oldCaption = old.element.querySelector('.caption');
    if (!caption || !oldCaption) return false;
    if (!block.text || !old.text || !markEditedText(caption, oldCaption)) caption.classList.add(ADDED_BLOCK_CLASS);
    return true;
  }
  if (block.kind !== 'row') return !!block.text && !!old.text && markEditedText(block.element, old.element);

  const cells = Array.from(block.element.children);
  const oldCells = Array.from(old.element.children);
  if (cells.length !== oldCells.length) return false;
  cells.forEach((cell, i) => {
    const before = textOf(oldCells[i]).trim();
    const after = textOf(cell).trim();
    if (before === after) return;
    if (!before || !after || !markEditedText(cell, oldCells[i])) cell.classList.add(ADDED_BLOCK_CLASS);
  });
  return true;
};

// ── Removed blocks ──────────────────────────────────────────────────────────────────────────────

/** An added block is an edit of a removed one when they are the same kind of thing. */
const canPair = (block: Block, old: Block) => {
  if (block.kind !== old.kind) return false;
  if (block.kind === 'photo' || block.kind === 'image') return block.image === old.image; // a new image is a new photo
  if (block.kind === 'row') return block.cells === old.cells; // rows of the same table shape
  return block.element.tagName === old.element.tagName;
};

/** Puts a removed block back, struck through, next to `anchor`. */
const putBack = (doc: Document, removed: Block, anchor: Element, position: 'before' | 'after') => {
  const copy = doc.importNode(removed.element, true) as Element;
  copy.classList.add(REMOVED_BLOCK_CLASS);
  copy.removeAttribute('id');
  copy.removeAttribute(FIRST_CHANGE_ATTRIBUTE);
  anchor.parentNode?.insertBefore(copy, position === 'after' ? anchor.nextSibling : anchor);
  return copy;
};

/** A removed photo card: a red "Photo removed" box in the image's place, its caption struck through. */
const removedPhotoCard = (doc: Document, removed: Block) => {
  const card = doc.importNode(removed.element, true) as Element;
  card.removeAttribute(FIRST_CHANGE_ATTRIBUTE);
  const frame = card.querySelector('.image-frame') ?? card;
  const box = doc.createElement('div');
  box.className = REMOVED_PHOTO_BOX_CLASS;
  box.textContent = 'Photo removed';
  if (frame === card) card.prepend(box);
  else frame.replaceChildren(box);
  card.querySelector('.caption')?.classList.add(REMOVED_BLOCK_CLASS);
  return card;
};

const markRemoved = (anchor: Element, text: string) => {
  anchor.classList.add(REMOVED_MARKER_CLASS);
  const title = anchor.getAttribute('title');
  anchor.setAttribute('title', `${title ? `${title} / ` : 'Removed: '}${text.slice(0, 300)}`);
};

/**
 * Returns `currentHtml` with its changes against `previousHtml` marked, and how many places changed.
 */
export const highlightChanges = (currentHtml: string, previousHtml: string): { html: string; changeCount: number } => {
  const parser = new DOMParser();
  const currentDoc = parser.parseFromString(currentHtml, 'text/html');
  const previousDoc = parser.parseFromString(previousHtml, 'text/html');
  wrapLooseFlexText(currentDoc);
  wrapLooseFlexText(previousDoc);
  const current = collectBlocks(currentDoc);
  const previous = collectBlocks(previousDoc);
  const pairs = matchBlocks(current, previous);

  let changeCount = 0;
  let firstChange: Element | undefined;

  // Between two aligned pairs lies one change: what the current document has there replaced what
  // the previous one had.
  for (let k = 0; k <= pairs.length; k++) {
    const added = current.slice(k === 0 ? 0 : pairs[k - 1][0] + 1, k < pairs.length ? pairs[k][0] : current.length);
    const removed = previous.slice(k === 0 ? 0 : pairs[k - 1][1] + 1, k < pairs.length ? pairs[k][1] : previous.length);
    if (added.length === 0 && removed.length === 0) continue;
    changeCount++;

    // Pair each added block with the next removed block it can be an edit of (kept in order).
    const paired = new Set<Block>();
    let searchFrom = 0;
    added.forEach(block => {
      const index = removed.findIndex((old, i) => i >= searchFrom && !paired.has(old) && canPair(block, old));
      if (index >= 0 && markEditedBlock(block, removed[index])) {
        searchFrom = index + 1;
        paired.add(removed[index]);
        return;
      }
      block.element.classList.add(block.kind === 'photo' ? ADDED_PHOTO_CLASS : ADDED_BLOCK_CLASS);
    });
    firstChange ??= added[0]?.element;

    const previousAnchor = k > 0 ? current[pairs[k - 1][0]] : undefined;
    const nextAnchor = k < pairs.length ? current[pairs[k][0]] : undefined;
    const unpaired = removed.filter(old => !paired.has(old));

    // Removed photos: a red card in their own slot of the grid, ahead of what replaced them.
    const firstAddedPhoto = added.find(block => block.kind === 'photo');
    unpaired
      .filter(old => old.kind === 'photo')
      .forEach(old => {
        const card = removedPhotoCard(currentDoc, old);
        const before = firstAddedPhoto ?? (nextAnchor?.kind === 'photo' ? nextAnchor : undefined);
        const after = previousAnchor?.kind === 'photo' ? previousAnchor : undefined;
        const grid = currentDoc.querySelector('.photos-grid');
        if (before) before.element.parentNode?.insertBefore(card, before.element);
        else if (after) after.element.parentNode?.insertBefore(card, after.element.nextSibling);
        else if (grid) grid.appendChild(card);
        else return;
        firstChange ??= card;
      });

    // Other removed blocks go back, struck through, where they were: after the last block of the
    // same shape in this stretch, else next to the nearest unchanged one (rows only among rows of the
    // same table shape, so the HTML stays valid). Images are never re-inserted (fixed-size frames).
    // Otherwise the nearest block gets a red rule.
    const sameShape = (a: Block, b: Block) => a.kind === b.kind && (a.kind !== 'row' || a.cells === b.cells);
    const lastPlaced = new Map<string, Element>();
    unpaired
      .filter(old => old.kind !== 'photo')
      .forEach(old => {
        const shape = `${old.kind}:${old.cells}`;
        let placed: Element | undefined;
        if (old.kind !== 'image') {
          const after = lastPlaced.get(shape) ?? [...added].reverse().find(block => sameShape(block, old))?.element;
          if (after) placed = putBack(currentDoc, old, after, 'after');
          else if (previousAnchor && sameShape(previousAnchor, old)) placed = putBack(currentDoc, old, previousAnchor.element, 'after');
          else if (nextAnchor && sameShape(nextAnchor, old)) placed = putBack(currentDoc, old, nextAnchor.element, 'before');
        }
        if (placed) {
          lastPlaced.set(shape, placed);
          firstChange ??= placed;
          return;
        }
        const marker = (added[0] ?? nextAnchor ?? previousAnchor)?.element;
        if (!marker) return;
        markRemoved(marker, old.text || '(a photo)');
        firstChange ??= marker;
      });
  }

  firstChange?.setAttribute(FIRST_CHANGE_ATTRIBUTE, 'true');

  const style = currentDoc.createElement('style');
  style.textContent = HIGHLIGHT_STYLES;
  currentDoc.head.appendChild(style);

  return { html: `<!DOCTYPE html>${currentDoc.documentElement.outerHTML}`, changeCount };
};
